import { useState } from "react";
import { ArrowRight, Check, Clock, MapPin, Ruler } from "lucide-react";
import GlobalHeader from "./components/GlobalHeader";
import GlobalFooter from "./components/GlobalFooter";
import StudioHero from "./components/StudioHero";

const packages = [
  {
    id: "design",
    title: "Interior Design",
    image: "https://images.unsplash.com/photo-1617104678098-de229db51175?auto=format&fit=crop&w=1000&q=85",
    points: ["Space planning", "Materials and palette", "Furniture and lighting plan", "Install direction"],
  },
  {
    id: "staging",
    title: "Home Staging",
    image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1000&q=85",
    points: ["Walk-through edit", "Rental or owned inventory plan", "Art and accessories", "Photo-ready styling"],
  },
  {
    id: "curation",
    title: "Furniture Curation",
    image: "https://images.unsplash.com/photo-1616046229478-9901c5536a45?auto=format&fit=crop&w=1000&q=85",
    points: ["Sourcing shortlist", "Vintage and trade options", "Finish coordination", "Purchase roadmap"],
  },
];

export default function InteriorDesignPage() {
  const [emailDraft, setEmailDraft] = useState("");
  function prepareInquiry(event) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const subject = `${values.get("service")} inquiry from ${values.get("name")}`;
    const body = `Name: ${values.get("name")}\nEmail: ${values.get("email")}\nService: ${values.get("service")}\n\n${values.get("notes")}`;
    setEmailDraft(`mailto:hello@saunterhaus.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  }
  return (
    <div className="studio-site">
      <GlobalHeader />

      <main id="main-content" tabIndex={-1}>
        <StudioHero studio />
        <section className="studio-intro studio-shell">
          <p className="eyebrow">A personal approach</p>
          <div className="intro-columns">
            <h2>A feeling of home.<br />A sense of belonging.</h2>
            <div className="intro-copy"><p>We begin with your home and the life unfolding within it. What you love, what you need, and the pieces that already tell your story.</p><p>From a complete interior to a beautifully resolved room, our work brings together thoughtful planning, expressive materials, and furniture with lasting character.</p><a href="#contact" className="text-link">Tell us about your space <ArrowRight size={16} /></a></div>
          </div>
        </section>

        <section id="services" className="studio-service-packages services-band px-6 py-16 md:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="mb-10 max-w-3xl">
              <p className="eyebrow mb-3">Our services</p>
              <h2 className="font-serif text-4xl leading-tight">A room. A home. A new chapter.</h2>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {packages.map((item) => (
                <article key={item.id} id={item.id}>
                  <div className="aspect-[5/4] overflow-hidden bg-[#e8e0d3]">
                    <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
                  </div>
                  <div className="pt-7 pr-6">
                    <h3 className="font-serif text-3xl">{item.title}</h3>
                    <ul className="service-points mt-6 space-y-3 text-sm">
                      {item.points.map((point) => (
                        <li key={point} className="flex gap-3">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#76684f]" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="process" className="photo-band process-band">
          <img className="photo-backdrop" src="/images/saunter-services-room.png" alt="" aria-hidden="true" loading="lazy" />
          <div className="studio-shell process-layout">
            <div>
              <p className="eyebrow mb-3">Process</p>
              <h2 className="font-serif text-4xl leading-tight">Good rooms begin<br />with listening.</h2>
            </div>
            <div className="process-steps">
              {[
                ["01", "Discovery", "We map goals, budget, timeline, measurements, and what already needs to stay."],
                ["02", "Direction", "You receive a concept, palette, room priorities, and the first sourcing strategy."],
                ["03", "Curation", "We refine furniture, lighting, textiles, art, and styling pieces into a cohesive plan."],
                ["04", "Installation", "We coordinate final placement, styling, and the punch list that makes it feel complete."],
              ].map(([step, title, copy]) => (
                <article key={step}>
                  <p className="text-xs uppercase">{step}</p>
                  <h3 className="mt-5 font-serif text-2xl">{title}</h3>
                  <p className="mt-3 text-sm leading-6">{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="styling" className="styling-band px-6 py-16 md:py-20">
          <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-3">
            {[
              [Clock, "The finishing touches", "A thoughtful styling day brings art, objects, and textiles into balance with what is already there."],
              [Ruler, "Measured decisions", "Scale, proportion, and circulation drive the furnishing plan before anything is sourced."],
              [MapPin, "Local and remote", "Use the studio for in-home projects, virtual curation, or pre-listing staging direction."],
            ].map(([Icon, title, copy]) => (
              <article key={title} className="border-l border-[#b9ad9a] pl-5">
                <Icon className="mb-6 h-6 w-6 text-[#6f6251]" />
                <h3 className="font-serif text-2xl">{title}</h3>
                <p className="mt-3 text-sm leading-6 text-[#5d554a]">{copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="contact" className="photo-band inquiry-band">
          <img className="photo-backdrop" src="/images/saunter-notebook-details.png" alt="" aria-hidden="true" loading="lazy" />
          <div className="studio-shell inquiry-layout">
          <div className="inquiry-intro">
            <p className="eyebrow mb-3">Inquire</p>
            <h2 className="font-serif text-4xl leading-tight">Every home has a story.<br />Tell us yours.</h2>
            <p className="mt-5 text-sm leading-7">
              Share a little about your space, what you have in mind, and where you would like to begin.
            </p>
          </div>
          <form aria-label="Project inquiry" aria-describedby="inquiry-help" onSubmit={prepareInquiry} onChange={() => setEmailDraft("")} className="inquiry-form grid gap-4 p-6">
            <label htmlFor="inquiry-name">Name (required)</label>
            <input id="inquiry-name" name="name" autoComplete="name" required className="border bg-white px-4 py-3 text-sm" />
            <label htmlFor="inquiry-email">Email (required)</label>
            <input id="inquiry-email" name="email" type="email" autoComplete="email" required className="border bg-white px-4 py-3 text-sm" />
            <label htmlFor="inquiry-service">Service</label>
            <select id="inquiry-service" name="service" className="border bg-white px-4 py-3 text-sm">
              <option>Interior design</option>
              <option>Home staging</option>
              <option>Furniture curation</option>
              <option>Styling day</option>
            </select>
            <label htmlFor="inquiry-notes">Project notes</label>
            <textarea id="inquiry-notes" name="notes" className="min-h-[140px] border bg-white px-4 py-3 text-sm" />
            <p id="inquiry-help" className="text-sm leading-6">Prepare a draft to review and send in your email app.</p>
            <button type="submit" className="inline-flex items-center justify-center gap-2 px-5 py-3 text-sm uppercase text-white">
              Prepare inquiry <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </button>
            <div role="status">{emailDraft && <p className="text-sm leading-6">Your draft is ready. <a className="underline" href={emailDraft}>Open email draft</a></p>}</div>
          </form>
          </div>
        </section>
      </main>

      <GlobalFooter />
    </div>
  );
}
